import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Box,
  Grid,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from '@ipc/ipc-components';
import CloseIcon from '@mui/icons-material/Close';
import { Column } from 'react-table';
import { format, parseISO } from 'date-fns';
import { useImpcCodes } from '../../../../common/hooks/useImpcCodes';

// Define types for the IMPC code API response
interface ImpcCode {
  impc?: string;
  name?: string;
  id?: number;
  exchangeOfficeName?: string;
  exchangeOfficeId?: number;
  excludeFromUnex?: boolean;
}

interface PaginatedResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalPages: number;
  totalElements: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
}

// E53-specific data interfaces
interface E53BaseData {
  sampleId: string;
  version: number;
  changeBy: string;
  comment: boolean | string;
  delFlag: number;
  validFlag: number;
  mailCategory: string;
  formatCorrected: string;
  formatAnnounced: string;
  samplingMethod: string;
  netOrGrossAccounting: string;
  samplingRate: number;
  sendingPpo: string;
  oeFromCode: string;
  receivingPpo: string;
  oeToCode: string;
  samplingDate: string;
  receptacleNumber: number;
  despatchNumber: number;
  despatchDate: string;
  despatchMailSubclass: string;
  grossKg: number;
  tareKg: number;
  netKg: number;
  labelWeight: number;
  ibrsKg: number;
  pkg: number;
  gkg: number;
  ekg: number;
  ckg: number;
  pitems: number;
  gitems: number;
  eitems: number;
  citems: number;
  receptacleType: string;
  receptacleCategory: string;
  receptacleMailSubclass: string;
  subsampleRate: number;
  barcode: string;
  rv: number;
  fbag: number;
}

interface E53Sample extends E53BaseData {
  id: number;
  sampleDbId?: number;
  fileId?: number;
}

interface NewE53Sample extends E53BaseData {
  isNew: true;
  id?: string;
  fileId?: number;
}

type E53SampleWithNew = E53Sample | NewE53Sample;

interface E53EditModalProps<T extends object> {
  isOpen: boolean;
  onClose: () => void;
  row: T | null;
  columns: Column<T>[];
  onSave: (editedRow: T, originalRow: T) => Promise<boolean> | boolean;
  readOnlyColumns?: string[];
  title?: string;
  requiredFields?: string[];
  textAreaColumns?: string[];
  dateColumns?: string[];
  defaultReadOnlyColumns?: string[];
  priorityReadOnlyColumns?: string[];
  excludedColumns?: string[];
  showErrorsImmediately?: boolean;
}

const E53EditModal = <T extends object>({
  isOpen,
  onClose,
  row,
  columns,
  onSave,
  readOnlyColumns = [],
  excludedColumns = [],
  requiredFields = [],
  textAreaColumns = [],
  dateColumns = [],
  defaultReadOnlyColumns = [],
  priorityReadOnlyColumns = [],
  title = 'Edit E53 Sample',
  showErrorsImmediately = true
}: E53EditModalProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  
  const [editedRow, setEditedRow] = useState<T | null>(null);
  const [originalRow, setOriginalRow] = useState<T | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState(false);

  // E53-specific: Fetch IMPC codes for oeFromCode and oeToCode dropdowns
  const receivingPpo = row ? (row as any).receivingPpo : null;
  const sendingPpo = row ? (row as any).sendingPpo : null;
  
  const {
    data: oeFromCodeOptions,
    isLoading: isLoadingOeFromCodes
  } = useImpcCodes({ designatedOperator: sendingPpo, enabled: isOpen && !!sendingPpo, pageSize: 500 });
  
  const {
    data: oeToCodeOptions,
    isLoading: isLoadingOeToCodes
  } = useImpcCodes({ designatedOperator: receivingPpo, enabled: isOpen && !!receivingPpo, pageSize: 500 });

  // Convert IMPC codes array to dropdown options
  const oeFromCodeDropdownOptions = useMemo(() => {
    if (!oeFromCodeOptions?.data) return [];
    
    if (Array.isArray(oeFromCodeOptions.data)) {
      return oeFromCodeOptions.data
        .map((option: any) => option?.impc || '')
        .filter(Boolean);
    }
    
    return [];
  }, [oeFromCodeOptions]);

  const oeToCodeDropdownOptions = useMemo(() => {
    if (!oeToCodeOptions?.data) return [];
    
    if (Array.isArray(oeToCodeOptions.data)) {
      return oeToCodeOptions.data
        .map((option: any) => option?.impc || '')
        .filter(Boolean);
    }
    
    return [];
  }, [oeToCodeOptions]);

  // Combine default read-only columns with user-specified ones
  const allReadOnlyColumns = useMemo(() => {
    return [...defaultReadOnlyColumns, ...readOnlyColumns];
  }, [readOnlyColumns]);

  // Initialize edited row when modal opens with a row
  useEffect(() => {
    if (row && isOpen) {
      const formattedRow = { ...row };
      
      // E53-specific formatting: Format delFlag to display value (0 -> "No", 1 -> "Yes")
      if ((formattedRow as any).delFlag !== undefined) {
        (formattedRow as any).delFlag = (formattedRow as any).delFlag === 1 ? 'Yes' : 'No';
      }
      
      // E53-specific formatting: Format comment field - convert false boolean to empty string
      if ((formattedRow as any).comment === false) {
        (formattedRow as any).comment = '';
      }
      
      // E53-specific formatting: Format numeric weight fields to preserve decimal places (2 or 3 decimals)
      ['grossKg', 'tareKg', 'netKg', 'ibrsKg', 'pkg', 'gkg', 'ekg', 'ckg'].forEach(field => {
        if ((formattedRow as any)[field] !== undefined && 
            (formattedRow as any)[field] !== null && 
            (formattedRow as any)[field] !== '') {
          const value = (formattedRow as any)[field];
          const numValue = Number(value);
          
          if (!isNaN(numValue)) {
            if (numValue === 0) {
              (formattedRow as any)[field] = "0.00";
            } else if (value.toString().includes('.')) {
              const [whole, decimal] = value.toString().split('.');
              if (decimal.length === 1) {
                (formattedRow as any)[field] = `${whole}.${decimal}0`;
              }
            } else if (Number.isInteger(numValue)) {
              (formattedRow as any)[field] = `${value}.00`;
            }
          }
        }
      });
      
      // E53-specific formatting: Format labelWeight to 1 decimal place
      if ((formattedRow as any).labelWeight !== undefined && 
          (formattedRow as any).labelWeight !== null && 
          (formattedRow as any).labelWeight !== '') {
        const value = (formattedRow as any).labelWeight;
        const numValue = Number(value);
        
        if (!isNaN(numValue)) {
          // Format to exactly 1 decimal place
          (formattedRow as any).labelWeight = numValue.toFixed(1);
        }
      }
      
      // E53-specific formatting: Format subsampleRate to 4 decimal places
      if ((formattedRow as any).subsampleRate !== undefined && 
          (formattedRow as any).subsampleRate !== null && 
          (formattedRow as any).subsampleRate !== '') {
        const value = (formattedRow as any).subsampleRate;
        const numValue = Number(value);
        
        if (!isNaN(numValue)) {
          // Format to exactly 4 decimal places
          (formattedRow as any).subsampleRate = numValue.toFixed(4);
        }
      }
      
      // E53-specific formatting: Format date fields to YYYYMMDD format if they are in ISO format
      ['samplingDate', 'despatchDate'].forEach(field => {
        if ((formattedRow as any)[field] && 
            typeof (formattedRow as any)[field] === 'string' && 
            (formattedRow as any)[field].includes('-')) {
          try {
            const isoDate = parseISO((formattedRow as any)[field]);
            if (!isNaN(isoDate.getTime())) {
              (formattedRow as any)[field] = format(isoDate, 'yyyyMMdd');
            }
          } catch (error) {
            console.error(`Error formatting date field ${field}:`, error);
          }
        }
      });
      
      setEditedRow(formattedRow);
      setOriginalRow(row);
      
      // Initialize validation errors map
      const initialErrors: Record<string, string> = {};
      for (const field of requiredFields) {
        if (field in formattedRow) {
          const value = (formattedRow as any)[field];
          if (value === null || value === undefined || value === '') {
            initialErrors[field] = 'Required';
          }
        }
      }
      setValidationErrors(initialErrors);
    }
  }, [row, isOpen]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setEditedRow(null);
      setOriginalRow(null);
      setIsSaving(false);
      setValidationErrors({});
      setHasSaveBeenAttempted(false);
    }
  }, [isOpen]);

  // Handle cell value change with E53-specific validation
  const handleCellChange = (column: string, value: any) => {
    if (!editedRow) return;
    
    if (excludedColumns?.includes(column)) {
      return;
    }
    
    const errors = { ...validationErrors };
    
    // Check if this is a required field
    if (requiredFields.includes(column)) {
      if (value === null || value === undefined || value === '') {
        errors[column] = 'Required';
      } else {
        delete errors[column];
      }
    }
    
    // E53-specific validation for number fields
    if (['despatchNumber', 'receptacleNumber', 'samplingRate'].includes(column)) {
      const numValue = Number(value);
      if (value !== '' && (!Number.isInteger(numValue) || numValue < 0)) {
        errors[column] = 'Must be a non-negative integer';
      } else {
        delete errors[column];
      }
    }
    
    // E53-specific validation for weight fields (2 or 3 decimals)
    else if (['grossKg', 'tareKg', 'netKg', 'ibrsKg', 'pkg', 'gkg', 'ekg', 'ckg'].includes(column)) {
      const numValue = Number(value);
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Must be a valid number';
      } else if (value !== '') {
        const valueStr = value.toString();
        const decimalStr = valueStr.split('.')[1] || '';
        
        // Must have exactly 2 or 3 decimal places
        if (!valueStr.includes('.') || (decimalStr.length !== 2 && decimalStr.length !== 3)) {
          errors[column] = 'Must have exactly 2 or 3 decimal places';
        } else {
          delete errors[column];
        }
      } else {
        delete errors[column];
      }
    }
    
    // E53-specific validation for labelWeight (exactly 1 decimal)
    else if (column === 'labelWeight') {
      const numValue = Number(value);
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Must be a valid number';
      } else if (value !== '') {
        const valueStr = value.toString();
        const decimalStr = valueStr.split('.')[1] || '';
        
        // Must have exactly 1 decimal place
        if (!valueStr.includes('.') || decimalStr.length !== 1) {
          errors[column] = 'Must have exactly 1 decimal place';
        } else {
          delete errors[column];
        }
      } else {
        delete errors[column];
      }
    }
    
    // E53-specific validation for item count fields
    else if (['pitems', 'gitems', 'eitems', 'citems'].includes(column)) {
      const numValue = Number(value);
      if (value !== '' && (!Number.isInteger(numValue) || numValue < 0)) {
        errors[column] = 'Must be a non-negative integer';
      } else {
        delete errors[column];
      }
    }
    
    // E53-specific validation for subsample rate (exactly 4 decimals)
    else if (column === 'subsampleRate') {
      const numValue = Number(value);
      if (value !== '' && isNaN(numValue)) {
        errors[column] = 'Must be a valid number';
      } else if (value !== '') {
        const valueStr = value.toString();
        const decimalStr = valueStr.split('.')[1] || '';
        
        // Must have exactly 4 decimal places
        if (!valueStr.includes('.') || decimalStr.length !== 4) {
          errors[column] = 'Must have exactly 4 decimal places';
        } else {
          delete errors[column];
        }
      } else {
        delete errors[column];
      }
    }
    
    const updatedRow = {
      ...editedRow,
      [column]: value
    };
    
    setEditedRow(updatedRow);
    setValidationErrors(errors);
  };

  // Handle date value change
  const handleDateChange = (column: string, date: Date | null) => {
    if (!editedRow) return;
    
    if (excludedColumns?.includes(column)) {
      return;
    }
    
    const errors = { ...validationErrors };
    const formattedDate = date ? format(date, 'yyyyMMdd') : null;
    
    if (requiredFields.includes(column)) {
      if (formattedDate === null) {
        errors[column] = 'Required';
      } else {
        delete errors[column];
      }
    } else {
      delete errors[column];
    }
    
    const updatedRow = {
      ...editedRow,
      [column]: formattedDate
    };
    
    setEditedRow(updatedRow);
    setValidationErrors(errors);
  };

  // Handle save button click with E53-specific processing
  const handleSave = async () => {
    if (!editedRow || !originalRow) {
      return;
    }
    
    setHasSaveBeenAttempted(true);
    
    // Final validation before saving
    const errors: Record<string, string> = {};
    
    // Check for required fields that are empty
    for (const field of requiredFields) {
      if (field in editedRow) {
        const value = (editedRow as any)[field];
        if (value === null || value === undefined || value === '') {
          errors[field] = 'Required';
        }
      }
    }
    
    // E53-specific: Convert delFlag from display value to numeric value
    if (typeof (editedRow as any).delFlag !== 'undefined') {
      (editedRow as any).delFlag = (editedRow as any).delFlag === 'Yes' ? 1 : 0;
    }
    
    // If there are validation errors, don't submit
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      setIsSaving(true);
      const result = await Promise.resolve(onSave(editedRow, originalRow));
      if (result) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving E53 sample:', error);
      setValidationErrors({ 
        _global: 'Failed to save sample. Please try again.' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Get all fields, ordered by priority
  const getOrderedColumns = () => {
    if (!columns) return [] as Column<T>[];
    
    const priorityColumns = columns.filter(col => 
      typeof col.accessor === 'string' && 
      priorityReadOnlyColumns.includes(col.accessor) &&
      !excludedColumns?.includes(col.accessor)
    ).sort((a, b) => {
      const aAccessor = a.accessor as string;
      const bAccessor = b.accessor as string;
      return priorityReadOnlyColumns.indexOf(aAccessor) - 
             priorityReadOnlyColumns.indexOf(bAccessor);
    });
    
    const commentColumn = columns.find(col => 
      typeof col.accessor === 'string' && 
      textAreaColumns.includes(col.accessor) &&
      !excludedColumns?.includes(col.accessor)
    );
    
    const otherColumns = columns.filter(col => 
      typeof col.accessor === 'string' && 
      !priorityReadOnlyColumns.includes(col.accessor) &&
      !textAreaColumns.includes(col.accessor) &&
      !excludedColumns?.includes(col.accessor) &&
      col.accessor !== 'actions'
    );
    
    return [
      ...priorityColumns,
      ...(commentColumn ? [commentColumn] : []),
      ...otherColumns
    ];
  };

  // Render field based on column type with E53-specific configurations
  const renderField = (column: Column<T>) => {
    if (!editedRow) return null;
    
    const accessor = column.accessor as string;
    
    if (excludedColumns?.includes(accessor) || 
        excludedColumns?.includes(column.Header?.toString().toLowerCase() || '')) {
      return null;
    }
    
    const columnHeader = column.Header?.toString().toLowerCase() || '';
    
    // E53-specific: Define which fields are allowed to be displayed
    const allowedFields = [
      'sampleId', 'sample_id',
      'version',
      'changeBy', 'change_by',
      'comment', 'comments',
      'delFlag', 'del_flag',
      'validFlag', 'valid_flag',
      'mailCategory', 'mail_category',
      'formatCorrected', 'format_corrected',
      'formatAnnounced', 'format_announced',
      'samplingMethod', 'sampling_method',
      'netOrGrossAccounting', 'net_or_gross_accounting',
      'samplingRate', 'sampling_rate',
      'oeFromCode', 'oe_from_code',
      'oeToCode', 'oe_to_code',
      'samplingDate', 'sampling_date',
      'receptacleNumber', 'receptacle_number',
      'despatchNumber', 'despatch_number',
      'despatchDate', 'despatch_date',
      'grossKg', 'gross_kg',
      'tareKg', 'tare_kg',
      'netKg', 'net_kg',
      'pkg', 'p_kg',
      'gkg', 'g_kg',
      'ekg', 'e_kg',
      'ckg', 'c_kg',
      'pitems', 'p_items',
      'gitems', 'g_items',
      'eitems', 'e_items',
      'citems', 'c_items',
      'despatchMailSubclass', 'despatch_mail_subclass',
      'receptacleType', 'receptacle_type',
      'receptacleCategory', 'receptacle_category',
      'subsampleRate', 'subsample_rate',
      'barcode',
      'rv',
      'fbag', 'f_bag',
      'receptacleMailSubclass', 'receptacle_mail_subclass',
      'labelWeight', 'label_weight',
      'ibrsKg', 'ibrs_kg'
    ];

    // E53-specific: Define which fields are disabled/read-only
    const disabledFields = [
      'sampleId', 'sample_id',
      'version',
      'validFlag', 'valid_flag'
    ];

    // E53-specific: For new samples, disable additional fields
    const isNewSample = row && (row as any).isNew;
    const disabledForNewSample = isNewSample ? [
      'delFlag', 'del_flag',
      'comment', 'comments'
    ] : [];

    // E53-specific: ONLY these fields are editable, all others are read-only
    const editableFields = [
      'mailCategory', 'mail_category',
      'formatCorrected', 'format_corrected',
      'samplingMethod', 'sampling_method',
      'netOrGrossAccounting', 'net_or_gross_accounting',
      'samplingRate', 'sampling_rate',
      'oeFromCode', 'oe_from_code',
      'oeToCode', 'oe_to_code',
      'comment', 'comments',
      'delFlag', 'del_flag',
      'samplingDate', 'sampling_date',
      'receptacleNumber', 'receptacle_number',
      'despatchNumber', 'despatch_number',
      'despatchDate', 'despatch_date',
      'grossKg', 'gross_kg',
      'tareKg', 'tare_kg',
      'netKg', 'net_kg',
      'pkg', 'p_kg',
      'gkg', 'g_kg',
      'ekg', 'e_kg',
      'ckg', 'c_kg',
      'pitems', 'p_items',
      'gitems', 'g_items',
      'eitems', 'e_items',
      'citems', 'c_items',
      'despatchMailSubclass', 'despatch_mail_subclass',
      'receptacleType', 'receptacle_type',
      'receptacleCategory', 'receptacle_category',
      'subsampleRate', 'subsample_rate',
      'barcode',
      'rv',
      'fbag', 'f_bag',
      'receptacleMailSubclass', 'receptacle_mail_subclass',
      'labelWeight', 'label_weight',
      'ibrsKg', 'ibrs_kg',
      'formatAnnounced', 'format_announced'
    ];
    
    // Check if this field is allowed to be displayed
    const isFieldAllowed = allowedFields.includes(accessor) || allowedFields.includes(columnHeader);
    
    // Skip fields that are not in the allowed list
    if (!isFieldAllowed) {
      return null;
    }
    
    // Check if this field is one of the explicitly editable fields
    const isFieldEditable = editableFields.includes(accessor) || editableFields.includes(columnHeader);
    
    // Check if this field should be disabled
    const isFieldDisabled = disabledFields.includes(accessor) || disabledFields.includes(columnHeader) ||
                            disabledForNewSample.includes(accessor) || disabledForNewSample.includes(columnHeader);
    
    // Field is read-only unless it's explicitly in the editable list, or if it's a disabled field
    const isReadOnly = !isFieldEditable || allReadOnlyColumns.includes(accessor) || priorityReadOnlyColumns.includes(accessor) || isFieldDisabled;
    
    const isTextArea = textAreaColumns.includes(accessor) || accessor === 'comment' || accessor === 'comments';
    const isDateField = dateColumns.includes(accessor) || 
                        columnHeader.includes('sampling_date') || 
                        columnHeader.includes('despatch_date');
    const value = (editedRow as any)[accessor] !== undefined ? (editedRow as any)[accessor] : '';
    
    const isRequiredAndEmpty = requiredFields.includes(accessor) && 
                              (value === null || value === undefined || value === '');

    const hasError = isDateField
      ? (showErrorsImmediately || hasSaveBeenAttempted) && !!validationErrors[accessor]
      : (showErrorsImmediately || hasSaveBeenAttempted) && (!!validationErrors[accessor] || isRequiredAndEmpty);
    
    const isRequired = requiredFields.includes(accessor);
    const fieldLabel = isRequired ? `${column.Header} *` : column.Header;
    const fieldTestId = `e53-edit-modal-field-${accessor}`;

    const isPriorityField = priorityReadOnlyColumns.includes(accessor);
    
    // E53-specific: Handle number fields with integer validation
    if (['despatchNumber', 'receptacleNumber', 'samplingRate', 'pitems', 'gitems', 'eitems', 'citems', 'rv', 'fbag'].includes(accessor)) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText={hasError ? validationErrors[accessor] : ""}
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            min: 0,
            step: ['samplingRate', 'subsampleRate'].includes(accessor) ? 0.0001 : 1
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }

    // E53-specific: Handle weight fields with decimal validation (2 or 3 decimals)
    if (['grossKg', 'tareKg', 'netKg', 'ibrsKg', 'pkg', 'gkg', 'ekg', 'ckg'].includes(accessor)) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText={hasError ? validationErrors[accessor] : ""}
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            step: 0.01
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // E53-specific: Handle subsampleRate with 4 decimal validation
    if (accessor === 'subsampleRate') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText={hasError ? validationErrors[accessor] : ""}
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            step: 0.0001
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // E53-specific: Handle labelWeight with 1 decimal validation
    if (accessor === 'labelWeight') {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText={hasError ? validationErrors[accessor] : ""}
          fullWidth
          margin="dense"
          size="small"
          type="number"
          inputProps={{ 
            step: 0.1
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // E53-specific dropdown fields
    const e53DropdownFields: Record<string, string[]> = {
      'mailCategory': ['A', 'B', 'C', 'D'],
      'formatCorrected': ['P', 'G', 'E', 'X', 'J', 'Z', 'C', 'S'],
      'formatAnnounced': ['C', 'S', 'P', 'G', 'E', 'X', 'N', 'J', 'Z'],
      'samplingMethod': ['continuous', 'continuous co', 'continuous pr', 'continuous rc', 'continuous sm'],
      'netOrGrossAccounting': ['G'],
      'oeFromCode': isLoadingOeFromCodes 
        ? ['Loading...'] 
        : oeFromCodeDropdownOptions,
      'oeToCode': isLoadingOeToCodes 
        ? ['Loading...'] 
        : oeToCodeDropdownOptions,
      'delFlag': ['No', 'Yes'],
      'despatchMailSubclass': ['UA', 'UE', 'UL', 'UN', 'UP', 'UY', 'UZ'],
      'receptacleType': ['BG', 'CG', 'CN', 'FW', 'GU', 'IB', 'IL', 'IS', 'PB', 'PU', 'PX'],
      'receptacleCategory': ['bag', 'tray', 'pallet', 'container', 'dummy_fully_count_bag', 'dummy_fully_count_container', 'dummy_fully_count_pallet', 'dummy_fully_count_tray', 'dummy_'],
      'receptacleMailSubclass': ['UA', 'UE', 'UL', 'UN', 'UP', 'UX', 'UY', 'UZ']
    };
    
    // Check if the current field should be a dropdown
    const isDropdown = accessor in e53DropdownFields || 
                       columnHeader.includes('mail_category') || 
                       columnHeader.includes('format_corrected') ||
                       columnHeader.includes('format_announced') ||
                       columnHeader.includes('sampling_method') ||
                       columnHeader.includes('net_or_gross_accounting') ||
                       columnHeader.includes('oe_from_code') || 
                       columnHeader.includes('oe_to_code') || 
                       accessor === 'delFlag' || 
                       columnHeader.includes('del_flag') ||
                       columnHeader.includes('despatch_mail_subclass') ||
                       columnHeader.includes('receptacle_type') ||
                       columnHeader.includes('receptacle_category') ||
                       columnHeader.includes('receptacle_mail_subclass');

    // For textarea fields (comments)
    if (isTextArea) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          onChange={(e) => handleCellChange(accessor, e.target.value)}
          disabled={isReadOnly}
          error={hasError}
          helperText={hasError ? validationErrors[accessor] : ""}
          fullWidth
          margin="dense"
          size="small"
          multiline
          rows={3}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 1,
            bgcolor: isReadOnly ? '#f5f5f5' : 'white',
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // For dropdown fields
    if (isDropdown) {
      // Special handling for delFlag
      if (accessor === 'delFlag' || columnHeader.includes('del_flag')) {
        return (
          <FormControl 
            fullWidth 
            margin="dense" 
            size="small"
            error={hasError}
            disabled={isReadOnly}
            variant="outlined"
            sx={{ 
              minWidth: '100%',
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
              },
              mb: 0.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: isReadOnly ? '#f5f5f5' : 'white'
              }
            }}
            data-testid={fieldTestId}
          >
            <InputLabel id={`${accessor}-label`}>{fieldLabel}</InputLabel>
            <Select
              labelId={`${accessor}-label`}
              value={value || 'No'}
              onChange={(e) => handleCellChange(accessor, e.target.value)}
              label={fieldLabel}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 224,
                    width: 'auto',
                    marginTop: 8
                  },
                },
              }}
              data-testid={`${fieldTestId}-select`}
            >
              <MenuItem value="No" data-testid={`${fieldTestId}-option-no`}>No</MenuItem>
              <MenuItem value="Yes" data-testid={`${fieldTestId}-option-yes`}>Yes</MenuItem>
            </Select>
          </FormControl>
        );
      }
      
      // Get options for this dropdown
      let options = typeof accessor === 'string' && accessor in e53DropdownFields 
        ? e53DropdownFields[accessor] 
        : [];
      
      // For oeFromCode or oeToCode, ensure the current value is in the options list
      if ((accessor === 'oeFromCode' || accessor === 'oeToCode') && 
          value && !options.includes(value) && !isLoadingOeFromCodes && !isLoadingOeToCodes) {
        options = [...options, value];
      }
      
      const isLoading = (accessor === 'oeFromCode' && isLoadingOeFromCodes) || 
                       (accessor === 'oeToCode' && isLoadingOeToCodes);
      
      return (
        <FormControl 
          fullWidth 
          margin="dense" 
          size="small"
          error={hasError}
          disabled={isReadOnly || isLoading}
          variant="outlined"
          sx={{ 
            minWidth: '100%',
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
            },
            mb: 0.5,
            '& .MuiOutlinedInput-root': {
              bgcolor: isReadOnly ? '#f5f5f5' : 'white'
            }
          }}
          data-testid={fieldTestId}
        >
          <InputLabel id={`${accessor}-label`}>{fieldLabel}</InputLabel>
          <Select
            labelId={`${accessor}-label`}
            value={value || ''}
            onChange={(e) => handleCellChange(accessor, e.target.value)}
            label={fieldLabel}
            startAdornment={isLoading ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 224,
                  width: 'auto',
                  marginTop: 8
                },
              },
            }}
            data-testid={`${fieldTestId}-select`}
          >
            {(['despatchMailSubclass', 'receptacleMailSubclass', 'receptacleType'].includes(accessor)) && 
              <MenuItem value="" data-testid={`${fieldTestId}-option-none`}><em>None</em></MenuItem>}
            {options.map((option: string) => (
              <MenuItem key={option} value={option} data-testid={`${fieldTestId}-option-${option}`}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    // For priority read-only fields
    if (isPriorityField) {
      return (
        <TextField
          label={fieldLabel}
          value={value}
          disabled={true}
          error={hasError}
          helperText={hasError ? validationErrors[accessor] : ""}
          fullWidth
          margin="dense"
          size="small"
          sx={{
            '& .MuiInputBase-input': {
              fontWeight: 'bold',
              fontSize: '0.9rem',
            },
            mb: 1,
            bgcolor: '#f5f5f5',
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f5f5f5'
            }
          }}
          data-testid={fieldTestId}
        />
      );
    }
    
    // For date fields
    if (isDateField) {
      let dateValue = null;
      if (value && typeof value === 'string') {
        try {
          if (value.includes('-')) {
            dateValue = parseISO(value);
          } else {
            const year = parseInt(value.substring(0, 4));
            const month = parseInt(value.substring(4, 6)) - 1;
            const day = parseInt(value.substring(6, 8));
            dateValue = new Date(year, month, day);
          }
          
          if (isNaN(dateValue.getTime())) {
            dateValue = null;
          }
        } catch (error) {
          dateValue = null;
        }
      }
      
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={fieldLabel}
            value={dateValue}
            onChange={(newDate) => handleDateChange(accessor, newDate)}
            disabled={isReadOnly}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "dense",
                size: "small",
                error: hasError,
                helperText: hasError ? validationErrors[accessor] : "",
                sx: {
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                  mb: 0.5,
                  bgcolor: isReadOnly ? '#f5f5f5' : 'white',
                  '& .MuiOutlinedInput-root': {
                    bgcolor: isReadOnly ? '#f5f5f5' : 'white'
                  }
                }
              }
            }}
          />
        </LocalizationProvider>
      );
    }
    
    // For regular text fields
    return (
      <TextField
        label={fieldLabel}
        value={value}
        onChange={(e) => handleCellChange(accessor, e.target.value)}
        disabled={isReadOnly}
        error={hasError}
        helperText={hasError ? validationErrors[accessor] : ""}
        fullWidth
        margin="dense"
        size="small"
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
          },
          mb: 0.5,
          bgcolor: isReadOnly ? '#f5f5f5' : 'white',
          '& .MuiOutlinedInput-root': {
            bgcolor: isReadOnly ? '#f5f5f5' : 'white'
          }
        }}
        data-testid={fieldTestId}
      />
    );
  };

  const orderedColumns = getOrderedColumns();

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      data-testid="e53-edit-modal"
    >
      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
        data-testid="e53-edit-modal-title"
      >
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
          data-testid="e53-edit-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent 
        dividers 
        sx={{ 
          p: 2, 
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
        data-testid="e53-edit-modal-content"
      >
        {validationErrors._global && (
          <Typography color="error" variant="body2" sx={{ mb: 1.5 }} data-testid="e53-edit-modal-global-error">
            {validationErrors._global}
          </Typography>
        )}
        
        {/* Priority Fields Section */}
        {orderedColumns.filter(col => 
          typeof col.accessor === 'string' && 
          priorityReadOnlyColumns.includes(col.accessor)
        ).length > 0 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              mb: 2, 
              bgcolor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}
            data-testid="e53-edit-modal-priority-section"
          >
            <Grid container spacing={2}>
              {orderedColumns
                .filter(col => 
                  typeof col.accessor === 'string' && 
                  priorityReadOnlyColumns.includes(col.accessor)
                )
                .map((column) => (
                  <Grid item xs={12} sm={4} key={column.accessor as string}>
                    {renderField(column)}
                  </Grid>
                ))}
            </Grid>
          </Paper>
        )}
        
        {/* Comments Field */}
        {(orderedColumns.filter(col => 
          typeof col.accessor === 'string' && 
          (textAreaColumns.includes(col.accessor) || col.accessor === 'comment')
        ).length > 0) && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              mb: 2,
              bgcolor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.12)'
            }}
            data-testid="e53-edit-modal-comments-section"
          >
            <Grid container spacing={2}>
              {orderedColumns
                .filter(col => 
                  typeof col.accessor === 'string' && 
                  (textAreaColumns.includes(col.accessor) || col.accessor === 'comment')
                )
                .map((column) => (
                  <Grid item xs={12} sm={6} key={column.accessor as string}>
                    {renderField(column)}
                  </Grid>
                ))}
            </Grid>
          </Paper>
        )}

        {/* All Other Fields */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1.5,
            bgcolor: 'white',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            flex: 1,
            overflow: 'auto'
          }}
          data-testid="e53-edit-modal-fields-section"
        >
          <Grid container spacing={1}>
            {orderedColumns
              .filter(col => 
                typeof col.accessor === 'string' && 
                !priorityReadOnlyColumns.includes(col.accessor) && 
                !textAreaColumns.includes(col.accessor) &&
                col.accessor !== 'actions' &&
                col.accessor !== 'comment'
              )
              .map((column: Column<T>) => {
                const accessor = column.accessor as string;
                const fieldComponent = renderField(column);
                
                // Only render Grid item if the field component is not null
                if (!fieldComponent) {
                  return null;
                }
                
                return (
                  <Grid 
                    item 
                    xs={6} 
                    sm={4} 
                    md={3} 
                    lg={2} 
                    key={accessor}
                    data-testid={`e53-edit-modal-grid-item-${accessor}`}
                  >
                    {fieldComponent}
                  </Grid>
                );
              })
              .filter(Boolean)}
          </Grid>
        </Paper>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 1.5, gap: '16px', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }} data-testid="e53-edit-modal-actions">
        <Button 
          onClick={handleCancel}
          variant="secondary"
          size="small"
          data-testid="e53-edit-modal-cancel-button"
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={handleSave}
          variant="primary"
          size="small"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          data-testid="e53-edit-modal-save-button"
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default E53EditModal;
